import { useMemo, useRef } from "react";
import { useFirstMountState } from "react-use";
import { AbstractDate } from "@linkfluence/models/date";
import { getRangeFromDates } from "@tootools/date";
import { useSearchFragmentData } from "@radarly/core/fragment/use-cases";
import { getDateFromClause } from "@radarly/models/clause/getter";
import { refineClauseWithDate } from "@radarly/models/clause/refiner";
import {
  getComparaisonPeriodFromFragments,
  getTimezoneFromFragment,
  hasComparaisonFragment,
} from "@radarly/models/fragment";
import {
  AIFragment,
  SearchFragment,
  TimeUnitFragment,
} from "@radarly/models/search";

export function usePrevious<T>(data: T, isLoading: boolean) {
  const currentRef = useRef<T>(data);
  const isFirstMount = useFirstMountState();
  if (!isFirstMount && !isLoading) {
    currentRef.current = data;
  }
  return currentRef.current;
}

export function useRangeWithTimeUnit<F extends TimeUnitFragment>(
  fragment: F,
  loading: boolean,
  nowDate?: AbstractDate
) {
  const range = useRangeFromFragment(fragment, loading, nowDate);
  const { interval } = range;
  return {
    ...range,
    interval: fragment?.request?.aggs?.date?.interval || interval,
  };
}

export function useRangeFromFragment<F extends SearchFragment | AIFragment>(
  fragment: F,
  loading: boolean,
  nowDate?: AbstractDate
) {
  const timezone = getTimezoneFromFragment(fragment);
  const range = useMemo(() => {
    const { from, to } = getDateFromClause(fragment?.request.query);
    return getRangeFromDates(from, to, timezone, nowDate);
  }, [fragment?.request.query, timezone, nowDate]);
  const previous = usePrevious(range, loading);
  return previous;
}

export function useTimezoneFromFragment<F extends SearchFragment | AIFragment>(
  fragment: F
) {
  return useMemo(() => {
    return getTimezoneFromFragment(fragment);
  }, [fragment]);
}

export function useComparaisonPeriodFromFragments<
  F extends SearchFragment | AIFragment
>(fragment: F, comparaisonFragment: F, loading: boolean) {
  const _hasComparaisonFragment = hasComparaisonFragment(
    fragment,
    comparaisonFragment
  );
  return usePrevious(
    _hasComparaisonFragment
      ? getComparaisonPeriodFromFragments(fragment, comparaisonFragment)
      : null,
    loading
  );
}

export function useFragmentData<F extends SearchFragment | AIFragment>(
  project: number,
  fragment: F
) {
  const { loading, data } = useSearchFragmentData(project, fragment);
  const previousData = usePrevious(data, loading);
  return {
    loading,
    data: previousData,
  };
}

export const getValidComparisonFragment = <
  T extends SearchFragment | AIFragment
>(
  fragment: T,
  comparisonFragment?: T
) => {
  if (comparisonFragment) {
    /* SUPPORTLKF-603 SUPPORTLKF-730
    There have been issues with comparisonFragment's filters not kept in sync with fragment's filters
    This patch makes sure that all filters (except the date) are the same on both fragments
    Ideally once the source of those issues and the data has been fixed, this can be deleted
    */
    const previousDate = getDateFromClause(comparisonFragment.request.query);
    return {
      ...fragment,
      request: {
        ...fragment.request,
        id: comparisonFragment.request.id,
        query: refineClauseWithDate(fragment.request.query, previousDate),
      },
    };
  }
  return fragment;
};

export function useFragmentsData<T extends SearchFragment | AIFragment>(
  project: number,
  fragment: T,
  comparaisonFragment?: T
) {
  const { loading, data: fragmentData } = useSearchFragmentData(
    project,
    fragment
  );
  const { loading: comparaisonLoading, data: comparaisonFragmentData } =
    useSearchFragmentData(
      project,
      getValidComparisonFragment(fragment, comparaisonFragment)
    );
  const isLoading = loading || comparaisonLoading;
  return {
    data: usePrevious(fragmentData, isLoading),
    comparaisonData: usePrevious(comparaisonFragmentData, isLoading),
    loading: isLoading,
  };
}
